/* FQ-I4 · the plan ladder has one source: apps/api/src/lib/plans.js (prices, establishments, the participation ceiling,
   the trial), read here by relative path — vite's `server.fs.allow` already covers the repo root, the module is pure, and
   tsconfig's `allowJs` types it. Nothing here restates a number.
   « NO Growth » (Lyam, 08/10; gate-go-2026-10-08.md): V1 sells « Offre gratuite » and « Starter » only, and plans.js
   has no Growth row since Technical's plans-no-growth. `multi` stays for the nine-locations world, which is post-launch
   (nine-answers § 6). The names are the merchant's, in PLAN_LABELS, here (campaign.ts re-exports them).
   lib-pack-12: the campaigns a plan may have online at once come from plans.js too (`campaigns`, Technical's
   plans-campaigns). This file takes only a type from campaign.ts, so campaign.ts imports it with no runtime cycle. */
import { PLANS, EXTRA_LOCATION, TRIAL_DAYS, SOLD_PLAN_KEYS } from '../../../api/src/lib/plans.js'
import type { Plan } from './campaign'

/** The plans in the merchant's words: « Offre gratuite » · « Starter » · « Multi-établissements » (FQ-I4). */
export const PLAN_LABELS: Record<Plan, string> = { free: 'Offre gratuite', starter: 'Starter', multi: 'Multi-établissements' }

/** What V1 sells, in the ladder's order: plans.js's `SOLD_PLAN_KEYS` (billing-read, 10/10), never restated here (lib-pack-15). */
export const SOLD_PLANS: Plan[] = SOLD_PLAN_KEYS as Plan[]

export type Interval = 'monthly' | 'yearly' | '3y'
/** A price in cents, as plans.js holds it. */
export const planAmount = (p: Plan, interval: Interval = 'monthly'): number => PLANS[p].amounts[interval]
/** « 19 € », « 171 € », « 8,69 € »: whole euros without decimals. The « € » follows a no-break space (U+00A0), so a
    price never leaves its number at the end of a line (FQ-I4 vet, F1). */
export const eur = (cents: number): string =>
  `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}\u00A0€`
/** « 19,00 € », as an invoice prints it, with the same no-break space. */
export const eurExact = (cents: number): string =>
  `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u00A0€`
/** « 0 € », « 19 €/mois ». */
export const planPrice = (p: Plan): string => PLANS[p].billable ? `${eur(planAmount(p))}/mois` : eur(0)
/** Establishments included. */
export const planLocations = (p: Plan): number => PLANS[p].locations
/** The participation ceiling per establishment and month (an abuse ceiling, never a value lever — L5). */
export const planQuota = (p: Plan): number => PLANS[p].quotaPerLocation
/** The campaigns online at once (en ligne, programmées or en pause, as the API's plan_limit counts); null = no ceiling. */
export const planCampaigns = (p: Plan): number | null => PLANS[p].campaigns
/** Whether the plan has the card-backed trial (monthly and yearly only). */
export const planTrial = (p: Plan): boolean => PLANS[p].trial
export { TRIAL_DAYS }
/** An establishment beyond the plan's own (Multi only, in plans.js). */
export const extraLocationAmount = (interval: Interval = 'monthly'): number => EXTRA_LOCATION.amounts[interval]
/** The yearly and three-year discounts, from the prices themselves: « −25 % », « −40 % ». The « % » follows a no-break
    space (U+00A0), as « € » does (FQ-I4 follow-ups, found not built). */
export const discount = (p: Plan, interval: 'yearly' | '3y'): string => {
  const months = interval === 'yearly' ? 12 : 36
  return `−${Math.round((1 - planAmount(p, interval) / (planAmount(p) * months)) * 100)}\u00A0%`
}
/** « Offre gratuite », « Offre Starter »: the plan named in a sentence. */
export const planOffer = (p: Plan): string => p === 'free' ? PLAN_LABELS.free : `Offre ${PLAN_LABELS[p]}`
